/**
 * The agent's own working mark — the canonical one, and the design is prescriptive about it.
 *
 * `135F-0` captions it **`dot matrix + shimmer + elapsed`** and `148G-0` states the rule as a
 * do/don't: *"the agent itself, not a tool — leads with the dot matrix, never a sparkle."* So it is
 * the shared `ThinkingIndicatorUI`: the 25-circle diamond breathing and the turn clock. There is no
 * invented action label here. A status is shown only when the stream gives us real reasoning or a
 * real tool/action row; otherwise the matrix is enough to say that the agent is still working.
 *
 * ## The working mark is the canonical drawing
 *
 * THE GLYPH IS `DotmSquare11`, NOT `agent-dots`, and Guri caught the difference on sight.
 *
 * v1.5 authored `agent-dots.svg` because the registry's `agent.svg` is a sparkle and artboard 148G-0
 * is a hard rule against exactly that. Correct as far as it goes — but the product already HAD a dot
 * matrix, and it is a live one: `DotmSquare11` ripples its dots outward by Manhattan distance from
 * the centre, with phases and a reduced-motion path. Ours was a static SVG that faded the whole glyph
 * 1 → 0.45 and back. Both are "a dot matrix"; only one of them is the one every other thinking state
 * in this product shows, which is what "use exactly that one" meant.
 *
 * `ActivityGlyph state="running"` used to be a two-arc spinner, on the reasoning that this row says
 * *the agent* is working while that one says *this tool* is. Guri overruled it: *"don't use that
 * circle refresh icon anywhere — use the thinking indicator UI instead. Literally every state should
 * be that."* `STATE_ICON` now maps `running` and `deleting` onto the same 25-circle matrix, breathing
 * rather than rotating. What separates the two is no longer the drawing but the tone and the label —
 * a step inside a card is `subtle` and says what it is doing, while this row is the agent's own and
 * stays a neutral loader until the stream provides a real status.
 *
 * ## Honest waiting status
 *
 * A generic phrase such as "Planning the next step" is not evidence of what the agent is doing —
 * it is only a timer-shaped placeholder. The waiting state therefore says only "Thinking" and
 * reports the elapsed time. The stream's actual reasoning window and activity rows remain the
 * source of truth. The matrix is still part of that canonical waiting indicator — it identifies the
 * agent while the text and clock describe its state.
 */
import { ThinkingIndicatorUI } from '~/design-system/components/PlanningChat/ui/ThinkingIndicatorUI';
import { chatThemeClass, type ChatTheme } from '~/design-system/components/v1_5/Chat';
import { cn } from '~/lib/utils';
import { elapsedLabel } from '../turnModel';
import { useRollingThinkingText } from '~/design-system/components/PlanningChat/ui/useRollingThinkingText';
import { useSmoothText } from './useSmoothText';
import styles from './ThinkingIndicator.module.css';

/** Kept on the Deck 1.5 activity surface for existing callers. */
export { THINKING_LABEL } from '~/design-system/components/PlanningChat/ui/thinkingActions';

export interface ThinkingIndicatorProps {
  /**
   * Seconds since the TURN opened, not since reasoning started — `turn.elapsedSec()`. The clock is
   * the turn's, which is why it keeps counting through a tool run and does not reset when the model
   * starts a second block of reasoning.
   */
  elapsedSec: number;
  /**
   * The reasoning arriving right now, verbatim. Omit it and the row still says `Thinking` with the
   * turn clock, so a slow request never looks empty.
   */
  reasoning?: string;
  /**
   * The reasoning is a thought that already settled, kept in view while the model generates the call
   * it decided on. Drawn one tone down so it reads as context, not as something still being written.
   */
  stale?: boolean;
  theme: ChatTheme;
  className?: string;
}

export function ThinkingIndicator({ elapsedSec, reasoning, stale = false, theme, className }: ThinkingIndicatorProps) {
  const text = reasoning?.trim() ?? '';

  const row = (
    <ThinkingIndicatorUI
      text="Thinking"
      showDots
      showLoadingDots={false}
      elapsedText={elapsedLabel(elapsedSec)}
      className={cn(chatThemeClass(theme), !text && className)}
    />
  );

  // No reasoning: the indicator alone, and no wrapper. A group with one child would put this row
  // inside an 8px object when the artboard puts it straight into the 12px column.
  if (!text) return row;

  return (
    <div className={cn(chatThemeClass(theme), styles.group, className)}>
      {row}
      <ReasoningWindow text={text} theme={theme} stale={stale} />
    </div>
  );
}

interface ReasoningWindowProps {
  text: string;
  theme: ChatTheme;
  stale?: boolean;
}

/**
 * Three lines of the model's reasoning, pinned to the newest.
 *
 * The window clips itself rather than growing, so a turn that reasons for ninety seconds costs the
 * column 66px and not nine hundred. The content track is translated to the newest line and its CSS
 * transition is retargeted as tokens arrive, which gives a rolling motion without queuing scroll
 * animations for every token.
 *
 * Deliberately **not** a live region. The text changes several times a second; a polite region here
 * would be unusable, and the status row above already announces the work. The settled
 * "Thought for N sec" recap is where the reasoning becomes readable at rest — which is also why this
 * is not `aria-hidden`: the words are real content, they are simply not news.
 */
function ReasoningWindow({ text: arriving, theme, stale = false }: ReasoningWindowProps) {
  // Reasoning arrives as summaries in lumps; revealed at a steady pace it rolls instead of jumping.
  // A settled thought is already whole, so it is shown whole.
  const text = useSmoothText(arriving, !stale);
  const { viewport, track } = useRollingThinkingText(text);

  return (
    <div
      ref={viewport}
      className={cn(chatThemeClass(theme), styles.window)}
      data-stale={stale ? 'true' : undefined}
      // Cheap enough to be exact: one line has nothing above it to have rolled away, so the fade is
      // switched off rather than dimming the only line on screen.
      data-short={!text.includes('\n') && text.length < 60 ? 'true' : undefined}
    >
      <div ref={track} className={styles.textTrack}>
        <p className={styles.text}>{text}</p>
      </div>
    </div>
  );
}
